import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { http } from "../lib/api";

function formatDate(iso) {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${months[parseInt(m[2], 10) - 1]} ${parseInt(m[3], 10)}, ${m[1]}`;
}

// ─ Minimal, safe markdown renderer ────────────────────────────────
// Supports: paragraphs, **bold**, *italic*, [text](url), > blockquote,
// ### headings, - / 1. lists. No raw HTML allowed through.
function renderMarkdown(md) {
  if (!md) return [];
  const blocks = md.split(/\n{2,}/);
  const nodes = [];
  let listBuf = null;

  const flushList = () => {
    if (!listBuf) return;
    const { ordered, items } = listBuf;
    const Tag = ordered ? "ol" : "ul";
    nodes.push(
      <Tag key={`list-${nodes.length}`} className={`${ordered ? "list-decimal" : "list-disc"} ml-6 space-y-2 text-cream/80 leading-relaxed`}>
        {items.map((t, i) => <li key={i}>{inline(t)}</li>)}
      </Tag>
    );
    listBuf = null;
  };

  blocks.forEach((block, idx) => {
    const trimmed = block.trim();
    if (!trimmed) return;

    if (/^### /.test(trimmed)) {
      flushList();
      nodes.push(<h3 key={`h-${idx}`} className="font-serif text-2xl text-cream mt-10 mb-4">{inline(trimmed.replace(/^### /, ""))}</h3>);
      return;
    }
    if (/^> /.test(trimmed)) {
      flushList();
      nodes.push(<blockquote key={`q-${idx}`} className="border-l-4 border-gold/60 pl-5 py-1 my-6 text-cream/80 italic leading-relaxed">{inline(trimmed.replace(/^> /, ""))}</blockquote>);
      return;
    }
    if (/^- /.test(trimmed)) {
      const items = trimmed.split("\n").map((l) => l.replace(/^- /, ""));
      flushList();
      listBuf = { ordered: false, items };
      flushList();
      return;
    }
    if (/^\d+\. /.test(trimmed)) {
      const items = trimmed.split("\n").map((l) => l.replace(/^\d+\. /, ""));
      flushList();
      listBuf = { ordered: true, items };
      flushList();
      return;
    }
    flushList();
    nodes.push(<p key={`p-${idx}`} className="text-cream/85 leading-[1.85] text-[17px]">{inline(trimmed)}</p>);
  });
  flushList();
  return nodes;
}

// Inline: **bold**, *italic*, [text](url)
function inline(text) {
  const out = [];
  let rest = text;
  let key = 0;
  const patterns = [
    { re: /\[([^\]]+)\]\(([^)]+)\)/, fn: (m) => <a key={`a${key++}`} href={m[2]} target="_blank" rel="noopener noreferrer" className="link-gold underline underline-offset-4">{m[1]}</a> },
    { re: /\*\*([^*]+)\*\*/, fn: (m) => <strong key={`b${key++}`} className="text-cream">{m[1]}</strong> },
    { re: /\*([^*\n]+)\*/, fn: (m) => <em key={`i${key++}`} className="text-gold/90">{m[1]}</em> },
  ];
  while (rest.length) {
    let earliest = null;
    for (const p of patterns) {
      const m = rest.match(p.re);
      if (m && (earliest === null || m.index < earliest.m.index)) earliest = { p, m };
    }
    if (!earliest) {
      out.push(rest);
      break;
    }
    if (earliest.m.index > 0) out.push(rest.slice(0, earliest.m.index));
    out.push(earliest.p.fn(earliest.m));
    rest = rest.slice(earliest.m.index + earliest.m[0].length);
  }
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    http.get(`/blog/${slug}`)
      .then((r) => { setPost(r.data); setStatus("ok"); })
      .catch((e) => setStatus(e?.response?.status === 404 ? "notfound" : "error"));
  }, [slug]);

  usePageMeta({
    title: post ? `${post.title} - The Journal` : "The Journal",
    description: post ? post.body_md.replace(/[*_>#\n]+/g, " ").slice(0, 160) : "An entry from Queen Cleopatra's journal.",
    path: `/blog/${slug}`,
  });

  if (status === "notfound") return <Navigate to="/blog" replace />;
  if (status === "error") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="text-cream/60">Could not load this entry.</p>
        <Link to="/blog" className="link-gold mt-4 inline-block">← Back to the Journal</Link>
      </div>
    );
  }
  if (!post) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-cream/40 text-sm">Loading…</div>;
  }

  return (
    <article data-testid="page-blog-post" className="max-w-3xl mx-auto px-6 md:px-10 pt-20 pb-32">
      <Link to="/blog" className="link-gold text-xs uppercase tracking-[0.22em]">← The Journal</Link>

      <header className="mt-8 mb-10 border-b border-[#c9a04b]/20 pb-10">
        <div className="eyebrow mb-4 text-cream/55">
          {post.category || "The Journal"}
          {post.date && <> · {formatDate(post.date)}</>}
        </div>
        <h1 className="font-serif text-4xl md:text-5xl text-cream leading-[1.1]">{post.title}</h1>
      </header>

      <div className="space-y-6 blog-body">
        {renderMarkdown(post.body_md)}
      </div>

      {post.tags && post.tags.length > 0 && (
        <div className="mt-14 pt-8 border-t border-[#c9a04b]/15">
          <div className="eyebrow mb-3 text-cream/40">Filed under</div>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="text-[11px] uppercase tracking-wider border border-[#c9a04b]/30 px-3 py-1 text-cream/70">{t}</span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-16 text-center">
        <Link to="/blog" className="link-gold text-xs uppercase tracking-[0.22em]">← Back to all entries</Link>
      </div>
    </article>
  );
}
